import { APP_NAME, SUPPORT_EMAIL } from "../app.constants";

export interface LegalSection {
  title: string;
  paragraphs?: readonly string[];
  bullets?: readonly string[];
}

export interface LegalPage {
  slug: string;
  eyebrow: string;
  title: string;
  description: string;
  updated?: string;
  sections: readonly LegalSection[];
  email?: string;
  address?: readonly string[];
}

export const LEGAL_SUPPORT_PAGES: Record<string, LegalPage> = {
  "community-guidelines": {
    slug: "community-guidelines",
    eyebrow: "OUR COMMUNITY",
    title: "Community guidelines",
    description: `Help make ${APP_NAME} welcoming: be respectful, protect privacy, and never use the service to harm others.`,
    updated: "30 May 2026",
    sections: [
      {
        title: "Who these apply to",
        paragraphs: [
          `These guidelines apply to every person using ${APP_NAME} as a guest or registered user, from any country and on any device. By using the platform, you agree to follow them.`,
        ],
      },
      {
        title: "Immediate permanent bans",
        paragraphs: [
          "CSAM, use of the platform by minors, adult contact or grooming of minors, non-consensual intimate content, and credible threats of violence are absolute violations. They result in permanent bans and may require reporting to law enforcement.",
        ],
      },
      {
        title: "Harassment and abuse",
        bullets: [
          "Do not repeatedly target, insult, degrade, threaten, intimidate, or coerce another user.",
          "Do not continue unwanted contact after someone expresses disinterest or disconnects.",
          "Do not target users based on race, ethnicity, nationality, religion, gender, sexual orientation, or disability.",
        ],
      },
      {
        title: "Deception, spam, and privacy",
        bullets: [
          "Do not impersonate another person, staff member, or moderator.",
          "Do not misrepresent identity, age, or intentions to deceive or harm.",
          "Do not send spam, advertise, recruit, or use bots and automated tools.",
          "Do not share another person's private information or record them without consent.",
        ],
      },
      {
        title: "Respectful behavior",
        paragraphs: [
          "Treat the person you are talking to as a person. Be polite, respect boundaries, accept when someone ends a conversation, and use block, report, and disconnect tools instead of escalating conflict.",
        ],
      },
      {
        title: "Enforcement",
        paragraphs: [
          "Three reports from different users in 30 days trigger a formal review. Four confirmed violations result in a temporary ban, and five confirmed violations result in a permanent ban. Absolute violations are handled immediately regardless of prior history.",
        ],
      },
      {
        title: "Reporting",
        paragraphs: [
          `Use the in-platform report button. Reports are reviewed by our moderation team within 48 hours. Serious concerns can also be sent to ${SUPPORT_EMAIL}.`,
        ],
      },
      {
        title: "Updates",
        paragraphs: [
          "We may update these guidelines as the platform evolves. Continued use constitutes acceptance of the current guidelines.",
        ],
      },
    ],
    email: SUPPORT_EMAIL,
  },
  "cookie-policy": {
    slug: "cookie-policy",
    eyebrow: "TRANSPARENCY",
    title: "Cookie policy",
    description: `How ${APP_NAME} uses cookies and similar technologies to operate and improve the service.`,
    updated: "30 May 2026",
    sections: [
      {
        title: "What is a cookie?",
        paragraphs: [
          "A cookie is a small text file placed on your device when you visit a website. Cookies cannot run code or carry viruses. They help a website remember information about your visit.",
        ],
      },
      {
        title: "Similar technologies",
        paragraphs: [
          "We may use local storage for session preferences, short-lived session tokens to keep you connected, and pixel tags to confirm page loads and measure performance.",
        ],
      },
      {
        title: "Essential cookies",
        bullets: [
          "sl_session: maintains an active guest or logged-in session; expires at end of session.",
          "sl_auth: authentication token for registered accounts; expires after 30 days.",
          "sl_csrf: protects against cross-site request forgery; expires at end of session.",
          "sl_prefs: stores basic session preferences such as language; expires after one year.",
        ],
      },
      {
        title: "Security cookies",
        paragraphs: [
          "Cloudflare may use __cf_bm, cf_clearance, and _cfuvid for bot detection, security challenges, and fair rate limiting. These are used for legitimate platform security interests.",
        ],
      },
      {
        title: "Analytics and advertising",
        paragraphs: [
          "Google Analytics cookies such as _ga, _gid, and _gat help us understand aggregated usage. Google AdSense cookies such as IDE, DSID, and NID may be used where advertising is displayed. Analytics and advertising cookies are set only after consent where required.",
        ],
      },
      {
        title: "Preference cookies",
        bullets: [
          "sl_lang remembers language preference for one year.",
          "sl_cookie_consent records cookie choices for 12 months.",
          "sl_dismissed_notice remembers a dismissed notice for 30 days.",
        ],
      },
      {
        title: "How to control cookies",
        paragraphs: [
          `You can view, delete, or block cookies through your browser settings. Disabling essential cookies may prevent ${APP_NAME} from functioning correctly.`,
        ],
      },
      {
        title: "Updates and contact",
        paragraphs: [
          `We may update this policy and its Last Updated date. For questions, contact ${SUPPORT_EMAIL}.`,
        ],
      },
    ],
    email: SUPPORT_EMAIL,
  },
  contact: {
    slug: "contact",
    eyebrow: "GET IN TOUCH",
    title: "Contact us",
    description: `Questions, feedback, or a safety concern? The ${APP_NAME} team is here to help.`,
    sections: [
      {
        title: "Send us an email",
        paragraphs: [
          "Our support team reads every message and typically responds within 1–2 business days.",
        ],
      },
      {
        title: "What can we help you with?",
        bullets: [
          "Safety and reports: report inappropriate behavior, abuse, or content that violates our Community Guidelines.",
          "Technical issues: tell us about trouble with the app, matching, or chat.",
          "Feedback and ideas: share a feature idea or something that could be better.",
          "Business and partnerships: contact us about collaboration, press, or partnerships.",
        ],
      },
      {
        title: "Press and media",
        paragraphs: [
          "For press releases and media inquiries, reach out to us directly.",
        ],
      },
      {
        title: "Please include useful detail",
        paragraphs: [
          "Include as much detail as possible so we can help faster. For safety reports, include the approximate time and any other context you have.",
        ],
      },
    ],
    email: SUPPORT_EMAIL,
    address: [`${APP_NAME}`],
  },
};
